/**
 * Shared, translatable language options for subtitle generation.
 *
 * Both the upload flow (UploadDetailsPromptContent) and the media-page
 * CC modal (SubtitleModal.jsx) need the same list of WhisperX-supported
 * languages, with Finnish and English pinned to the top. Language names
 * are translated via a hardcoded map (practical for ~99 languages vs.
 * maintaining 99 i18n message IDs).
 *
 * Usage:
 *   import { getLanguageOptions } from 'volto-base-addon/helpers/languageOptions';
 *   const options = getLanguageOptions(locale);  // locale = 'fi' | 'en' | ...
 *   // → [{ code: 'fi', name: 'suomi' }, { code: 'en', name: 'englanti' }, ...]
 */

import { LANGUAGES_LIST } from '../constants/ISO639-1';
import { ACCEPTED_LANGUAGE_CODES } from '../constants/whisperxAcceptedLanguages';

/**
 * Finnish translations for language names.
 * Only the WhisperX-supported languages are included.
 * Falls back to the English name from ISO639-1 if not listed here.
 */
const LANGUAGE_NAMES_FI = {
  af: 'afrikaans',
  am: 'amhara',
  ar: 'arabia',
  as: 'assami',
  az: 'azerbaidžani',
  ba: 'baškiiri',
  be: 'valkovenäjä',
  bg: 'bulgaria',
  bn: 'bengali',
  bo: 'tiibet',
  br: 'bretoni',
  bs: 'bosnia',
  ca: 'katalaani',
  cs: 'tšekki',
  cy: 'kymri',
  da: 'tanska',
  de: 'saksa',
  el: 'kreikka',
  en: 'englanti',
  es: 'espanja',
  et: 'viro',
  eu: 'baski',
  fa: 'persia',
  fi: 'suomi',
  fo: 'fääri',
  fr: 'ranska',
  gl: 'galicia',
  gu: 'gudžarati',
  ha: 'hausa',
  haw: 'havaiji',
  he: 'heprea',
  hi: 'hindi',
  hr: 'kroatia',
  ht: 'haiti',
  hu: 'unkari',
  hy: 'armenia',
  id: 'indonesia',
  is: 'islanti',
  it: 'italia',
  ja: 'japani',
  jw: 'jaava',
  ka: 'georgia',
  kk: 'kazakki',
  km: 'khmer',
  kn: 'kannada',
  ko: 'korea',
  la: 'latina',
  lb: 'luxemburg',
  ln: 'lingala',
  lo: 'lao',
  lt: 'liettua',
  lv: 'latvia',
  mg: 'malagassi',
  mi: 'maori',
  mk: 'makedonia',
  ml: 'malajalam',
  mn: 'mongoli',
  mr: 'marathi',
  ms: 'malaiji',
  mt: 'malta',
  my: 'burma',
  ne: 'nepali',
  nl: 'hollanti',
  nn: 'norja (nynorsk)',
  no: 'norja',
  oc: 'oksitaani',
  pa: 'pandžabi',
  pl: 'puola',
  ps: 'paštu',
  pt: 'portugali',
  ro: 'romania',
  ru: 'venäjä',
  sa: 'sanskrit',
  sd: 'sindhi',
  si: 'sinhala',
  sk: 'slovakki',
  sl: 'sloveeni',
  sn: 'šona',
  so: 'somali',
  sq: 'albania',
  sr: 'serbia',
  su: 'sunda',
  sv: 'ruotsi',
  sw: 'swahili',
  ta: 'tamili',
  te: 'telugu',
  tg: 'tadžikki',
  th: 'thai',
  tk: 'turkmeeni',
  tl: 'tagalog',
  tr: 'turkki',
  tt: 'tataari',
  uk: 'ukraina',
  ur: 'urdu',
  uz: 'uzbekki',
  vi: 'vietnam',
  yi: 'jiddiš',
  yo: 'joruba',
  zh: 'kiina',
};

/**
 * English names for the WhisperX codes ISO 639-1 cannot express: `haw` exists
 * only in 639-2/3, and WhisperX uses the deprecated `jw` where ISO 639-1 has
 * `jv`. Labels match the backend vocabulary so both sides read the same.
 */
const LANGUAGE_NAMES_EXTRA = {
  haw: 'Hawaiian',
  jw: 'Javanese',
};

/**
 * Get the translated name for a language code.
 *
 * @param {string} code - WhisperX language code (ISO 639-1, plus haw/jw)
 * @param {string} locale - UI locale ('fi', 'en', etc.)
 * @returns {string} Translated language name
 */
export function getLanguageName(code, locale) {
  if (locale === 'fi' && LANGUAGE_NAMES_FI[code]) {
    return LANGUAGE_NAMES_FI[code];
  }
  return LANGUAGES_LIST[code]?.name || LANGUAGE_NAMES_EXTRA[code] || code;
}

/**
 * The language named in itself — "suomi", "Deutsch" — with the English name as
 * a fallback. Locale-independent by construction, which is why stored subtitle
 * titles use it rather than a translation.
 *
 * @param {string} code - ISO 639-1 code
 * @returns {string}
 */
export function getLanguageEndonym(code) {
  return (
    LANGUAGES_LIST[code]?.nativeName?.split(',')[0].trim() ||
    LANGUAGES_LIST[code]?.name ||
    LANGUAGE_NAMES_EXTRA[code] ||
    code
  );
}

/**
 * Build sorted language options for subtitle generation dropdowns.
 * Finnish and English are always pinned to the top.
 *
 * @param {string} locale - UI locale ('fi', 'en', etc.)
 * @returns {Array<{code: string, name: string}>}
 */
export function getLanguageOptions(locale) {
  const pinned = ['fi', 'en'];

  const filtered = ACCEPTED_LANGUAGE_CODES.filter(
    (code) => LANGUAGES_LIST[code] || LANGUAGE_NAMES_EXTRA[code],
  );

  const pinnedItems = pinned
    .filter((code) => filtered.includes(code))
    .map((code) => ({ code, name: getLanguageName(code, locale) }));

  const rest = filtered
    .filter((code) => !pinned.includes(code))
    .map((code) => ({ code, name: getLanguageName(code, locale) }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));

  return [...pinnedItems, ...rest];
}
